// src/features/assistant-connect/components/CopyableCommand.tsx

import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { CheckRounded as CheckIcon, ContentCopyRounded as CopyIcon } from '@mui/icons-material';

interface CopyableCommandProps {
  value: string;
  label?: string;
  /** A caption under the value (e.g. «déjelo en blanco»). */
  hint?: string;
}

/**
 * A value to paste into another application: an optional caption, the value in a monospace block
 * that wraps (never scrolls the page sideways) and a copy button with a 44px hit area. The block is
 * selectable, so copying by hand works when the clipboard is unavailable.
 */
export const CopyableCommand: React.FC<CopyableCommandProps> = ({ value, label, hint }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the clipboard may be unavailable; the value stays selectable */
    }
  };

  const action = translate(copied ? 'assistantConnect.copied' : 'assistantConnect.copy');

  return (
    <Box sx={{ minWidth: 0 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          pl: 1.5,
          pr: 0.5,
          py: 0,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.custom.radius.small,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          component="pre"
          sx={{
            flex: 1,
            minWidth: 0,
            m: 0,
            py: 0.75,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '0.8125rem',
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            userSelect: 'all',
          }}
        >
          {value}
        </Box>
        <Tooltip title={action}>
          <IconButton onClick={copy} aria-label={action} sx={{ width: 44, height: 44, flexShrink: 0 }}>
            {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <span role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {copied ? action : ''}
      </span>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

export default CopyableCommand;
